import {
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { HelperWrapper } from '../helper-wrapper'
import {
  useApproveAllConsumerScans,
  useCheckNewSideLanguageTranslations,
  useGenerateAndPostTweet,
  useItemsRemoveConfirm,
  useReplyToFollowedUsersTweet,
  useReplyToHashtagTweet,
  useReplyToMentionsTweet,
  useSendMissingDecryptedTx,
  useSendMissingEncryptedTx,
  useSendTgScanApprovedDailyMessage,
  useTrackfyCollectDataAndReply,
  useTrackfyCollectDataAndReplySingleTweet,
  useUpdateAccountNfts,
  useUpdateCointelegraphTrends,
  useUpdateCronJob,
  useUpdateEsgTrends,
  useUpdateProductNfts,
  useUpdateVectorStoreData,
  useUpdateXTrends,
} from 'src/hooks'
import { useGetCronJobs } from 'src/hooks/crone-jobs/use-get-cron-jobs.hook'
import { Loader } from '../loader'
import { CRON_JOBS_IDS, EConfirmModalTitles } from 'src/constants'
import { CronScheduleSelect } from './cron-schedule-select'
import { IUpdateCronJobDto } from 'src/types/cron-jobs.type'
import { EXBotPromptFields } from 'src/types'
import { useState } from 'react'
import { SelectChallengeModal } from './approve-consumer-scans/select-challenge-modal.component'
import { SelectTweetModal } from './setup-from-tweet/select-tweet-modal.component'

const CronJobsTable = () => {
  const theme = useTheme()

  const [isSelectChallengeModalOpen, setIsSelectChallengeModalOpen] = useState(false)
  const [isSelecTweetModalOpen, setIsTweetModalOpen] = useState(false)

  const { setRemoveConfirm, setIsLoadingModal } = useItemsRemoveConfirm()

  const { data: cronJobs, isLoading: isCronJobsLoading } = useGetCronJobs()
  const { mutateAsync: updateCronJob, isLoading: isUpdateCronJobsLoading } = useUpdateCronJob()

  const { mutateAsync: updateProductNfts, isLoading: isProductNftsLoading } = useUpdateProductNfts()
  const { mutateAsync: checkTranslations, isLoading: isTranslationsCheckLoading } =
    useCheckNewSideLanguageTranslations()
  const { mutateAsync: updateAccountNfts, isLoading: isNftLoading } = useUpdateAccountNfts()
  const { mutateAsync: generateAndPostTweet, isLoading: isPostTweetLoading } =
    useGenerateAndPostTweet()
  const { mutateAsync: replyToMentionsTweet, isLoading: isReplyToMentionsTweetLoading } =
    useReplyToMentionsTweet()
  const { mutateAsync: replyToHashtagTweet, isLoading: isReplyToHashtagLoading } =
    useReplyToHashtagTweet()
  const { mutateAsync: replyToFollowedUsersTweet, isLoading: isReplyToFollowedUsersLoading } =
    useReplyToFollowedUsersTweet()
  const { mutateAsync: updateXTrends, isLoading: isUpdateXTrendsLoading } = useUpdateXTrends()
  const { mutateAsync: updateEsgTrends, isLoading: isUpdateEsgTrendsLoading } = useUpdateEsgTrends()
  const { mutateAsync: updateCointelegraphTrends, isLoading: isUpdateCointelegraphTrendsLoading } =
    useUpdateCointelegraphTrends()
  const { mutateAsync: sendTgScanApprovedDailyMessage, isLoading: isTgDailyMessageLoading } =
    useSendTgScanApprovedDailyMessage()
  const { mutateAsync: approveAllConsumerScans, isLoading: isScansApprovalLoading } =
    useApproveAllConsumerScans()
  const { mutateAsync: sendMissingEncryptedTx, isLoading: isSendMissingEncryptedTxLoading } =
    useSendMissingEncryptedTx()
  const { mutateAsync: sendMissingDecryptedTx, isLoading: isSendMissingDecryptedTxLoading } =
    useSendMissingDecryptedTx()
  const { mutateAsync: updateVectorStoreData, isLoading: isUpdateVectorStoreDataLoading } =
    useUpdateVectorStoreData()
  const {
    mutateAsync: trackfyCollectDataAndReply,
    isLoading: isTrackfyCollectDataAndReplyLoading,
  } = useTrackfyCollectDataAndReply()
  const {
    mutateAsync: trackfyCollectDataAndReplySingle,
    isLoading: isTrackfyCollectDataAndReplySingleLoading,
  } = useTrackfyCollectDataAndReplySingleTweet()

  const handleSelectChallengeModalClose = () => {
    setIsSelectChallengeModalOpen(false)
  }

  const handleSelectTweetModalClose = () => {
    setIsTweetModalOpen(false)
  }

  const handleConfirmCrone = async (func: () => Promise<void>) => {
    if (!func) return

    setRemoveConfirm({
      handleConfirm: async () => {
        setIsLoadingModal(true)

        await func().finally(() => {
          setIsLoadingModal(false)
        })
      },
      customModal: {
        title: EConfirmModalTitles.EXECUTE_CRON_JOB,
        secondaryText: 'CRON Job will be executed manually',
      },
      itemsTitles: [],
      itemsDetails: [],
      itemsRelations: [],
      itemsType: '',
    })
  }

  const handleUpdateAccountNfts = async () => {
    try {
      await updateAccountNfts()
    } catch (error) {}
  }

  const handleUpdateProductNfts = async () => {
    try {
      await updateProductNfts()
    } catch (error) {}
  }

  const handleLanguageTranslations = async () => {
    await checkTranslations()
  }

  const handleXTrendsUpdate = async () => {
    await updateXTrends()
  }

  const handleEsgTrendsUpdate = async () => {
    await updateEsgTrends()
  }

  const handleCointelegraphTrendsUpdate = async () => {
    await updateCointelegraphTrends()
  }

  const handleClickTweet = async (promptName: EXBotPromptFields) => {
    try {
      await generateAndPostTweet(promptName)
    } catch (error) {}
  }

  const handleReplyToHashtagTweet = async () => {
    await replyToHashtagTweet()
  }

  const handleReplyToFollowedUsersTweet = async () => {
    await replyToFollowedUsersTweet()
  }

  const handleReplyToMentionsTweet = async () => {
    await replyToMentionsTweet()
  }

  const handleSendTgScanApprovedDailyMessage = async () => {
    await sendTgScanApprovedDailyMessage()
  }

  const handleApproveAllConsumerScans = async (challange: string | null) => {
    await approveAllConsumerScans(challange)
  }

  const handleTrackfyCollectDataAndReplySingle = async (tweetId: string) => {
    await trackfyCollectDataAndReplySingle(tweetId)
  }

  const handleSendMissingEncryptedTx = async () => {
    await sendMissingEncryptedTx()
  }

  const handleSendMissingDecryptedTx = async () => {
    await sendMissingDecryptedTx()
  }

  const handleUpdateCrone = async (id: string, data: IUpdateCronJobDto) => {
    await updateCronJob({ id, ...data })
  }

  const handleToggleStatus = async (id: string, data: IUpdateCronJobDto) => {
    await updateCronJob({ id, ...data })
  }

  const handleUpdateVectorStoreData = async () => {
    await updateVectorStoreData()
  }

  const handleTrackfyCollectDataAndReply = async () => {
    await trackfyCollectDataAndReply()
  }

  if (isCronJobsLoading || !cronJobs) {
    return <Loader />
  }

  return (
    <>
      <TableContainer sx={{ overflowX: 'auto', mb: '50px' }}>
        <Table sx={{ minWidth: '600px' }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <HelperWrapper t="cron-jobs-module-column" />
              </TableCell>
              <TableCell>
                <HelperWrapper t="cron-jobs-title-column" />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="cron-jobs-scheduled-column" />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="cron-jobs-status-column" />
              </TableCell>
              <TableCell align="center" sx={{ minWidth: '95px' }}>
                <HelperWrapper t="cron-jobs-log-only-column" />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="cron-jobs-actions-column" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cronJobs.map((item) => {
              return (
                <TableRow key={item.id}>
                  <TableCell>{item.module}</TableCell>
                  <TableCell>
                    {item.title}
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: theme.palette.custom.grey2,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <CronScheduleSelect
                      id={item.id}
                      isDisabled={
                        !item.modifiable ||
                        isUpdateCronJobsLoading ||
                        item.id === CRON_JOBS_IDS.TRACKFY_SETUP_DATA_FROM_TWEET
                      }
                      schedule={item.schedule}
                      handleUpdate={handleUpdateCrone}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={item.is_active ? 'Active' : 'Inactive'} arrow>
                      <div style={{ display: 'inline-block' }}>
                        <Switch
                          size="small"
                          checked={item.is_active}
                          disabled={
                            !item.modifiable ||
                            isUpdateCronJobsLoading ||
                            item.id === CRON_JOBS_IDS.TRACKFY_SETUP_DATA_FROM_TWEET
                          }
                          onChange={(e) =>
                            handleToggleStatus(item.id, { is_active: e.target.checked })
                          }
                          inputProps={{ 'aria-label': 'Update Status' }}
                        />
                      </div>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">
                    <div style={{ display: 'inline-block' }}>
                      <Switch
                        size="small"
                        checked={item.log_only_mode}
                        disabled={!item.modifiable || isUpdateCronJobsLoading}
                        onChange={(e) =>
                          handleToggleStatus(item.id, { log_only_mode: e.target.checked })
                        }
                        inputProps={{ 'aria-label': 'Update Log Only status' }}
                      />
                    </div>
                  </TableCell>

                  <TableCell sx={{ color: 'green' }} align="center">
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.X_POST_TWEET && (
                      <Button
                        disabled={isPostTweetLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleClickTweet(EXBotPromptFields.X_BOT_POST)
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.X_POST_TWEET_2 && (
                      <Button
                        disabled={isPostTweetLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleClickTweet(EXBotPromptFields.X_BOT_POST_2)
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.X_POST_TWEET_3 && (
                      <Button
                        disabled={isPostTweetLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleClickTweet(EXBotPromptFields.X_BOT_POST_3)
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.X_POST_TWEET_4 && (
                      <Button
                        disabled={isPostTweetLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleClickTweet(EXBotPromptFields.X_BOT_POST_4)
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.X_REPLY_TO_HASH_TAG_TWEET && (
                        <Button
                          disabled={isReplyToHashtagLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleReplyToHashtagTweet()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.X_REPLY_TO_FOLLOWED_USERS_TWEET && (
                        <Button
                          disabled={isReplyToFollowedUsersLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleReplyToFollowedUsersTweet()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.X_REPLY_TO_MENTIONS_TWEET && (
                        <Button
                          disabled={isReplyToMentionsTweetLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleReplyToMentionsTweet()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.UPDATE_ACCOUNT_NFTS && (
                      <Button
                        disabled={isNftLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleUpdateAccountNfts()
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.UPDATE_PRODUCT_NFTS && (
                      <Button
                        disabled={isProductNftsLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleUpdateProductNfts()
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.SIDE_LANGUAGES_TRANSLATIONS && (
                        <Button
                          disabled={isTranslationsCheckLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleLanguageTranslations()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.X_TRENDS && (
                      <Button
                        disabled={isUpdateXTrendsLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleXTrendsUpdate()
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.ESG_TODAY_TRENDS && (
                      <Button
                        disabled={isUpdateEsgTrendsLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleEsgTrendsUpdate()
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.COINTELEGRAPH_TRENDS && (
                        <Button
                          disabled={isUpdateCointelegraphTrendsLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleCointelegraphTrendsUpdate()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.TG_BOT_APPROVED_SCANS_DAILY && (
                        <Button
                          disabled={isTgDailyMessageLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleSendTgScanApprovedDailyMessage()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.S2E_CONSUMER_SCANS_APPROVAl && (
                        <Button
                          disabled={isScansApprovalLoading}
                          onClick={() => {
                            setIsSelectChallengeModalOpen(true)
                          }}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.S2E_SEND_MISSING_ENCRYPTED_TX && (
                        <Button
                          disabled={isSendMissingEncryptedTxLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleSendMissingEncryptedTx()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.S2E_SEND_MISSING_DECRYPTED_TX && (
                        <Button
                          disabled={isSendMissingDecryptedTxLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleSendMissingDecryptedTx()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually && item.id === CRON_JOBS_IDS.VECTOR_STORE_UPDATE && (
                      <Button
                        disabled={isUpdateVectorStoreDataLoading}
                        onClick={handleConfirmCrone.bind(null, async () => {
                          await handleUpdateVectorStoreData()
                        })}
                      >
                        Run
                      </Button>
                    )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.TRACKFY_FOLLOWED_USERS_DATA && (
                        <Button
                          disabled={isTrackfyCollectDataAndReplyLoading}
                          onClick={handleConfirmCrone.bind(null, async () => {
                            await handleTrackfyCollectDataAndReply()
                          })}
                        >
                          Run
                        </Button>
                      )}
                    {item.can_trigger_manually &&
                      item.id === CRON_JOBS_IDS.TRACKFY_SETUP_DATA_FROM_TWEET && (
                        <Button
                          disabled={isTrackfyCollectDataAndReplySingleLoading}
                          onClick={() => {
                            setIsTweetModalOpen(true)
                          }}
                        >
                          Run
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <SelectChallengeModal
        open={isSelectChallengeModalOpen}
        confirmModalHandler={setRemoveConfirm}
        handleClose={handleSelectChallengeModalClose}
        handleConfirmApprove={handleApproveAllConsumerScans}
      />
      <SelectTweetModal
        open={isSelecTweetModalOpen}
        confirmModalHandler={setRemoveConfirm}
        handleClose={handleSelectTweetModalClose}
        handleConfirmTweet={handleTrackfyCollectDataAndReplySingle}
      />
    </>
  )
}

export { CronJobsTable }
